import React, { useState, useEffect, useCallback } from 'react';
import { getApiConfigs, getApiConfig, createApiConfig, updateApiConfig, toggleApiStatus, getChargeSlabs, createChargeSlab, updateChargeSlab, deleteChargeSlab, getAllMerchants } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Plus, Edit, Trash2, Settings, Layers, DollarSign, Percent, ArrowRight, Code, Security, IndianRupee, Power, PowerOff, Users, Store } from 'lucide-react';

const PRICING_TYPES = [
  { value: 'FIXED_CHARGE', label: 'Fixed Charge per Call', desc: 'e.g., Penny Drop Rs 2.80' },
  { value: 'SLAB_BASED', label: 'Slab-Based Charges', desc: 'e.g., Payout - different rates by amount' },
  { value: 'COMMISSION', label: 'Commission Model', desc: 'e.g., BBPS - merchant earns commission' },
  { value: 'FIXED_ON_TRANSACTION', label: 'Fixed on Transaction', desc: 'e.g., Credit Card Rs 12 + GST' }
];

const API_CATEGORIES = ['PAYMENT', 'VERIFICATION', 'BBPS', 'UTILITY', 'DMT', 'OTHER'];

const ApiConfig = () => {
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApi, setSelectedApi] = useState(null);
  const [slabs, setSlabs] = useState([]);
  const [showApiDialog, setShowApiDialog] = useState(false);
  const [showSlabDialog, setShowSlabDialog] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  const [editingSlab, setEditingSlab] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [assignedMerchants, setAssignedMerchants] = useState([]);

  const defaultApiForm = {
    apiCode: '', apiName: '', apiCategory: 'PAYMENT', apiEndpoint: '', externalUrl: '',
    method: 'POST', pricingType: 'FIXED_CHARGE', fixedCharge: 0, gstPercentage: 18,
    commissionAmount: 0, commissionType: 'FIXED', tdsPercentage: 5,
    minAmount: 1, maxAmount: 100000, description: '', isActive: true
  };

  const defaultSlabForm = {
    minAmount: 0, maxAmount: 0, chargeType: 'FIXED', chargeValue: 0, gstPercentage: 18,
    commissionValue: 0, commissionType: 'FIXED', tdsPercentage: 5, priority: 0, description: '', isActive: true
  };

  const [apiForm, setApiForm] = useState(defaultApiForm);
  const [slabForm, setSlabForm] = useState(defaultSlabForm);

  useEffect(() => {
    loadApis();
    loadMerchants();
  }, []);

  const loadApis = async () => {
    setLoading(true);
    try {
      const res = await getApiConfigs();
      setApis(res.data || []);
    } catch (err) {
      showErrorToast('Failed to load APIs');
    } finally {
      setLoading(false);
    }
  };

  const loadMerchants = async () => {
    try {
      const res = await getAllMerchants();
      setMerchants(res.data || []);
    } catch (err) {
      console.error('Failed to load merchants:', err);
    }
  };

  const loadApiDetails = async (apiCode) => {
    try {
      const [apiRes, slabsRes] = await Promise.all([
        getApiConfig(apiCode),
        getChargeSlabs(apiCode)
      ]);
      setSelectedApi(apiRes.data);
      setSlabs(slabsRes.data || []);

      // Parse merchant IDs from external_username field
      if (apiRes.data.external_username) {
        const merchantIds = apiRes.data.external_username.split(',').filter(id => id.trim());
        setSelectedMerchants(merchantIds);

        // Get full merchant details for display
        const assignedMerchantDetails = merchants.filter(merchant =>
          merchantIds.includes(merchant.merchantId || merchant.id)
        );
        setAssignedMerchants(assignedMerchantDetails);
      } else {
        setAssignedMerchants([]);
        setSelectedMerchants([]);
      }
    } catch (err) {
      showErrorToast('Failed to load API details');
    }
  };

  const handleSelectApi = (api) => {
    setSelectedApi(api);
    loadApiDetails(api.apiCode);
  };

  const handleCreateApi = () => {
    setEditingApi(null);
    setApiForm(defaultApiForm);
    setSelectedMerchants([]);
    setShowApiDialog(true);
  };

  const handleEditApi = (api) => {
    setEditingApi(api);
    setApiForm({
      apiCode: api.apiCode,
      apiName: api.apiName,
      apiCategory: api.apiCategory || 'PAYMENT',
      apiEndpoint: api.apiEndpoint,
      externalUrl: api.externalUrl || '',
      method: api.method || 'POST',
      pricingType: api.pricingType,
      fixedCharge: api.fixedCharge || 0,
      gstPercentage: api.gstPercentage || 18,
      commissionAmount: api.commissionAmount || 0,
      commissionType: api.commissionType || 'FIXED',
      tdsPercentage: api.tdsPercentage || 5,
      minAmount: api.minAmount || 1,
      maxAmount: api.maxAmount || 100000,
      description: api.description || '',
      isActive: api.isActive
    });
    // Load assigned merchants for editing
    loadAssignedMerchants(api.apiCode);
    setShowApiDialog(true);
  };

  const loadAssignedMerchants = async (apiCode) => {
    try {
      const res = await getApiConfig(apiCode);
      if (res.data.external_username) {
        const merchantIds = res.data.external_username.split(',').filter(id => id.trim());
        setSelectedMerchants(merchantIds);
      } else {
        setSelectedMerchants([]);
      }
    } catch (err) {
      console.error('Failed to load assigned merchants:', err);
      setSelectedMerchants([]);
    }
  };

  const handleSubmitApi = async (e) => {
    e.preventDefault();
    try {
      // Include merchant IDs in external_username field
      const apiDataWithMerchants = {
        ...apiForm,
        external_username: selectedMerchants.length > 0 ? selectedMerchants.join(',') : null
      };

      if (editingApi) {
        await updateApiConfig(editingApi.apiCode, apiDataWithMerchants);
        showSuccessToast('API updated successfully');
      } else {
        await createApiConfig(apiDataWithMerchants);
        showSuccessToast('API created successfully');
      }
      setShowApiDialog(false);
      loadApis();
      if (selectedApi?.apiCode === apiForm.apiCode) {
        loadApiDetails(apiForm.apiCode);
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Failed to save API');
    }
  };

  const handleToggleStatus = async (api) => {
    try {
      await toggleApiStatus(api.apiCode, !api.isActive);
      showSuccessToast(`API ${api.isActive ? 'deactivated' : 'activated'}`);
      loadApis();
      if (selectedApi?.apiCode === api.apiCode) {
        loadApiDetails(api.apiCode);
      }
    } catch (err) {
      showErrorToast('Failed to update status');
    }
  };

  const handleCreateSlab = () => {
    setEditingSlab(null);
    setSlabForm(defaultSlabForm);
    setShowSlabDialog(true);
  };

  const handleEditSlab = (slab) => {
    setEditingSlab(slab);
    setSlabForm({
      minAmount: slab.minAmount,
      maxAmount: slab.maxAmount,
      chargeType: slab.chargeType,
      chargeValue: slab.chargeValue,
      gstPercentage: slab.gstPercentage || 18,
      commissionValue: slab.commissionValue || 0,
      commissionType: slab.commissionType || 'FIXED',
      tdsPercentage: slab.tdsPercentage || 5,
      priority: slab.priority || 0,
      description: slab.description || '',
      isActive: slab.isActive
    });
    setShowSlabDialog(true);
  };

  const handleSubmitSlab = async (e) => {
    e.preventDefault();
    try {
      if (editingSlab) {
        await updateChargeSlab(editingSlab.id, slabForm);
        showSuccessToast('Slab updated');
      } else {
        await createChargeSlab(selectedApi.apiCode, null, slabForm);
        showSuccessToast('Slab created');
      }
      setShowSlabDialog(false);
      loadApiDetails(selectedApi.apiCode);
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Failed to save slab');
    }
  };

  const handleDeleteSlab = async (slabId) => {
    if (!window.confirm('Delete this slab?')) return;
    try {
      await deleteChargeSlab(slabId);
      showSuccessToast('Slab deleted');
      loadApiDetails(selectedApi.apiCode);
    } catch (err) {
      showErrorToast('Failed to delete slab');
    }
  };

  const getPricingBadge = (type) => {
    const colors = {
      'FIXED_CHARGE': 'bg-blue-100 text-blue-800',
      'SLAB_BASED': 'bg-purple-100 text-purple-800',
      'COMMISSION': 'bg-green-100 text-green-800',
      'FIXED_ON_TRANSACTION': 'bg-orange-100 text-orange-800'
    };
    const labels = {
      'FIXED_CHARGE': 'Fixed',
      'SLAB_BASED': 'Slab',
      'COMMISSION': 'Commission',
      'FIXED_ON_TRANSACTION': 'Txn Fixed'
    };
    return <Badge className={colors[type]}>{labels[type]}</Badge>;
  };

  if (loading) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header with Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-blue-900">API Configuration</h1>
            <p className="text-blue-700 text-sm mt-1">Manage payment APIs and pricing for merchants</p>
          </div>
          {/* <Button onClick={handleCreateApi} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> Add New API
          </Button> */}
        </div>

        {/* Quick Flow Guide */}
        <div className="mt-4 grid grid-cols-4 gap-2">
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs font-semibold text-blue-600">1. Create API</div>
            <div className="text-xs text-gray-600">Setup service</div>
          </div>
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs font-semibold text-blue-600">2. Set Pricing</div>
            <div className="text-xs text-gray-600">Choose charge model</div>
          </div>
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs font-semibold text-blue-600">3. Add Slabs</div>
            <div className="text-xs text-gray-600">Amount ranges (if needed)</div>
          </div>
          <div className="bg-white rounded p-2 text-center">
            <div className="text-xs font-semibold text-blue-600">4. Enable</div>
            <div className="text-xs text-gray-600">For merchants</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* API List */}
        <div className="col-span-4 space-y-3">
          <Card>
            <CardHeader className="py-3 bg-gray-50">
              <CardTitle className="text-sm flex items-center">
                <Settings className="h-4 w-4 mr-2" />
                Configured APIs ({apis.length})
              </CardTitle>
              <p className="text-xs text-gray-600 mt-1">Click any API to view/edit details</p>
            </CardHeader>
            <CardContent className="p-0 max-h-[600px] overflow-y-auto">
              {apis.map((api) => (
                <div
                  key={api.id}
                  className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${selectedApi?.apiCode === api.apiCode ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                  onClick={() => handleSelectApi(api)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{api.apiName}</p>
                      <p className="text-xs font-mono text-gray-500">{api.apiCode}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={api.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                        {api.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {getPricingBadge(api.pricingType)}
                    </div>
                  </div>
                </div>
              ))}
              {apis.length === 0 && (
                <div className="p-6 text-center">
                  <Settings className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm font-medium">No APIs configured yet</p>
                  <p className="text-gray-400 text-xs mb-4">Start by adding your first payment API</p>
                  <Button onClick={handleCreateApi} size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" /> Add Your First API
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* API Details */}
        <div className="col-span-8">
          {selectedApi ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center">
                      <Code className="h-5 w-5 mr-2 text-blue-600" />
                      {selectedApi.apiName}
                    </CardTitle>
                    <p className="text-sm text-gray-500 font-mono mt-1">{selectedApi.apiCode}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEditApi(selectedApi)}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button
                      variant={selectedApi.isActive ? "destructive" : "default"}
                      size="sm"
                      onClick={() => handleToggleStatus(selectedApi)}
                    >
                      {selectedApi.isActive ? <PowerOff className="h-4 w-4 mr-1" /> : <Power className="h-4 w-4 mr-1" />}
                      {selectedApi.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="details">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="details">
                      <Settings className="h-4 w-4 mr-1" />
                      <span>Basic Info</span>
                    </TabsTrigger>
                    <TabsTrigger value="pricing">
                      <DollarSign className="h-4 w-4 mr-1" />
                      <span>Pricing Model</span>
                    </TabsTrigger>
                    <TabsTrigger value="slabs">
                      <Layers className="h-4 w-4 mr-1" />
                      <span>Charge Slabs ({slabs.length})</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="details" className="space-y-4 mt-4">
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <h4 className="font-medium text-blue-900 text-sm mb-2">API Information</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <InfoItem label="Category" value={selectedApi.apiCategory} />
                        <InfoItem label="Method" value={selectedApi.method} />
                        <InfoItem label="Endpoint" value={selectedApi.apiEndpoint} className="col-span-2 font-mono text-xs" />
                        {selectedApi.externalUrl && (
                          <InfoItem label="External URL" value={selectedApi.externalUrl} className="col-span-2 font-mono text-xs" />
                        )}
                        <InfoItem label="Min Amount" value={`₹${selectedApi.minAmount?.toLocaleString()}`} />
                        <InfoItem label="Max Amount" value={`₹${selectedApi.maxAmount?.toLocaleString()}`} />
                        <InfoItem label="Merchants Enabled" value={selectedApi.merchantCount || 0} />
                        <InfoItem label="Pricing Type" value={selectedApi.pricingTypeDisplay} />
                      </div>
                      {selectedApi.description && (
                        <div className="p-3 bg-gray-50 rounded text-sm">{selectedApi.description}</div>
                      )}

                      {/* Assigned Merchants Section */}
                      <div className="mt-4 p-3 bg-purple-50 rounded border border-purple-200">
                        <h5 className="font-medium text-purple-900 text-sm mb-2 flex items-center">
                          <Store className="h-4 w-4 mr-2" />
                          Assigned Merchants ({assignedMerchants.length})
                        </h5>
                        {assignedMerchants.length > 0 ? (
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {assignedMerchants.map((merchant) => (
                              <div key={merchant.merchantId || merchant.id} className="flex items-center justify-between text-xs bg-white rounded p-2 border">
                                <div>
                                  <span className="font-medium">{merchant.merchantName || merchant.name || 'N/A'}</span>
                                  <span className="text-gray-500 ml-2">({merchant.businessName || 'N/A'})</span>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {merchant.status || 'ACTIVE'}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-purple-700">No merchants assigned to this API yet</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="pricing" className="space-y-4 mt-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-medium mb-3 flex items-center text-green-900">
                        <IndianRupee className="h-4 w-4 mr-2" />
                        Pricing Configuration
                      </h4>
                      <div className="mb-3">
                        {getPricingBadge(selectedApi.pricingType)}
                        <span className="ml-2 font-medium">{selectedApi.pricingTypeDisplay}</span>
                      </div>

                      {(selectedApi.pricingType === 'FIXED_CHARGE' || selectedApi.pricingType === 'FIXED_ON_TRANSACTION') && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-white rounded border">
                            <span className="text-gray-500 text-sm">Fixed Charge</span>
                            <p className="text-xl font-bold">₹{selectedApi.fixedCharge?.toFixed(2)}</p>
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <span className="text-gray-500 text-sm">GST</span>
                            <p className="text-xl font-bold">{selectedApi.gstPercentage}%</p>
                          </div>
                          <div className="col-span-2 p-3 bg-blue-50 rounded border border-blue-200">
                            <span className="text-blue-600 text-sm">Total per call</span>
                            <p className="text-xl font-bold text-blue-700">
                              ₹{(selectedApi.fixedCharge * (1 + selectedApi.gstPercentage / 100)).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}

                      {selectedApi.pricingType === 'COMMISSION' && (
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-3 bg-white rounded border">
                            <span className="text-gray-500 text-sm">Commission</span>
                            <p className="text-xl font-bold text-green-600">
                              {selectedApi.commissionType === 'FIXED' ? '₹' : ''}{selectedApi.commissionAmount}
                              {selectedApi.commissionType === 'PERCENTAGE' ? '%' : ''}
                            </p>
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <span className="text-gray-500 text-sm">TDS</span>
                            <p className="text-xl font-bold text-red-600">-{selectedApi.tdsPercentage}%</p>
                          </div>
                          <div className="p-3 bg-green-50 rounded border border-green-200">
                            <span className="text-green-600 text-sm">Net Commission</span>
                            <p className="text-xl font-bold text-green-700">
                              ₹{(selectedApi.commissionAmount * (1 - selectedApi.tdsPercentage / 100)).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}

                      {selectedApi.pricingType === 'SLAB_BASED' && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <p className="text-sm text-yellow-800 font-medium">⚡ Slab-Based Pricing</p>
                          <p className="text-xs text-yellow-700 mt-1">Charges vary based on transaction amount. Configure slabs in the "Charge Slabs" tab above.</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="slabs" className="mt-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h4 className="font-medium text-purple-900">Charge Slabs Configuration</h4>
                          <p className="text-xs text-purple-700 mt-1">Define different charges for different amount ranges</p>
                        </div>
                        <Button size="sm" onClick={handleCreateSlab} className="bg-purple-600 hover:bg-purple-700">
                          <Plus className="h-4 w-4 mr-1" /> Add New Slab
                        </Button>
                      </div>

                      {slabs.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Amount Range</TableHead>
                              <TableHead>Charge Type</TableHead>
                              <TableHead>Charge</TableHead>
                              <TableHead>GST</TableHead>
                              <TableHead>Total</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="w-20">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {slabs.map((slab) => (
                              <TableRow key={slab.id}>
                                <TableCell className="font-mono">
                                  ₹{slab.minAmount.toLocaleString()} <ArrowRight className="h-3 w-3 inline mx-1" /> ₹{slab.maxAmount.toLocaleString()}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {slab.chargeType === 'FIXED' ? <DollarSign className="h-3 w-3 mr-1" /> : <Percent className="h-3 w-3 mr-1" />}
                                    {slab.chargeType}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-medium">
                                  {slab.chargeType === 'FIXED' ? `₹${slab.chargeValue}` : `${slab.chargeValue}%`}
                                </TableCell>
                                <TableCell>{slab.gstPercentage}%</TableCell>
                                <TableCell className="font-medium text-blue-600">
                                  {slab.chargeType === 'FIXED'
                                    ? `₹${(slab.chargeValue * (1 + slab.gstPercentage / 100)).toFixed(2)}`
                                    : `${slab.chargeValue}% + GST`
                                  }
                                </TableCell>
                                <TableCell>
                                  <Badge className={slab.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                                    {slab.isActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => handleEditSlab(slab)}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteSlab(slab.id)}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-center p-8 bg-purple-50 rounded-lg border-2 border-dashed border-purple-300">
                          <Layers className="h-16 w-16 mx-auto text-purple-300 mb-4" />
                          <h5 className="text-lg font-medium text-purple-900 mb-2">No Charge Slabs Yet</h5>
                          <p className="text-sm text-purple-700 mb-4">Add amount-based pricing for your API</p>
                          <div className="bg-white rounded p-3 mb-4 max-w-md mx-auto">
                            <p className="text-xs text-gray-600 font-medium mb-2">Example Slab Structure:</p>
                            <div className="text-xs text-gray-500 space-y-1">
                              <div>• ₹1 - ₹1,000: ₹5 + GST</div>
                              <div>• ₹1,001 - ₹10,000: ₹10 + GST</div>
                              <div>• ₹10,001+: ₹15 + GST</div>
                            </div>
                          </div>
                          <Button onClick={handleCreateSlab} className="bg-purple-600 hover:bg-purple-700">
                            <Plus className="h-4 w-4 mr-1" /> Create Your First Slab
                          </Button>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full flex items-center justify-center min-h-[400px] bg-gray-50">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <Settings className="h-10 w-10 text-blue-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select an API to Configure</h3>
                <p className="text-sm text-gray-600 mb-4">Choose from the list on the left to view and edit details</p>
                <div className="bg-white rounded p-3 max-w-sm mx-auto">
                  <p className="text-xs text-gray-500 font-medium mb-1">Quick Start:</p>
                  <p className="text-xs text-gray-400">1. Select API → 2. Review settings → 3. Edit if needed → 4. Enable for merchants</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* API Dialog */}
      <Dialog open={showApiDialog} onOpenChange={setShowApiDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Code className="h-5 w-5 mr-2 text-blue-600" />
              {editingApi ? 'Edit API Configuration' : 'Create New API'}
            </DialogTitle>
            <p className="text-sm text-gray-600">
              {editingApi ? 'Update API settings and pricing' : 'Add a new payment API for merchants'}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmitApi} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>API Code *</Label>
                <Input
                  value={apiForm.apiCode}
                  onChange={(e) => setApiForm({ ...apiForm, apiCode: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                  disabled={!!editingApi}
                  placeholder="e.g., BRANCHX_PAYOUT"
                  required
                />
              </div>
              <div>
                <Label>API Name *</Label>
                <Input
                  value={apiForm.apiName}
                  onChange={(e) => setApiForm({ ...apiForm, apiName: e.target.value })}
                  placeholder="e.g., BranchX Fund Transfer"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={apiForm.apiCategory} onValueChange={(v) => setApiForm({ ...apiForm, apiCategory: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {API_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Method</Label>
                <Select value={apiForm.method} onValueChange={(v) => setApiForm({ ...apiForm, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Endpoint *</Label>
              <Input
                value={apiForm.apiEndpoint}
                onChange={(e) => setApiForm({ ...apiForm, apiEndpoint: e.target.value })}
                placeholder="/api/jcentrix/v1/..."
                required
              />
            </div>

            <div>
              <Label>External URL (Optional)</Label>
              <Input
                value={apiForm.externalUrl}
                onChange={(e) => setApiForm({ ...apiForm, externalUrl: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="p-4 bg-blue-50 rounded-lg space-y-4 border border-blue-200">
              <div>
                <Label className="text-blue-900 font-medium">Pricing Type *</Label>
                <p className="text-xs text-blue-700 mb-2">Choose how merchants will be charged for using this API</p>
                <Select value={apiForm.pricingType} onValueChange={(v) => setApiForm({ ...apiForm, pricingType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRICING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <div>
                          <span>{t.label}</span>
                          <span className="text-xs text-gray-500 ml-2">({t.desc})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(apiForm.pricingType === 'FIXED_CHARGE' || apiForm.pricingType === 'FIXED_ON_TRANSACTION') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fixed Charge (₹)</Label>
                    <Input type="number" step="0.01" value={apiForm.fixedCharge} onChange={(e) => setApiForm({ ...apiForm, fixedCharge: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <Label>GST %</Label>
                    <Input type="number" step="0.1" value={apiForm.gstPercentage} onChange={(e) => setApiForm({ ...apiForm, gstPercentage: parseFloat(e.target.value) || 0 })} />
                  </div>
                </div>
              )}

              {apiForm.pricingType === 'COMMISSION' && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Commission Amount</Label>
                    <Input type="number" step="0.01" value={apiForm.commissionAmount} onChange={(e) => setApiForm({ ...apiForm, commissionAmount: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <Label>Commission Type</Label>
                    <Select value={apiForm.commissionType} onValueChange={(v) => setApiForm({ ...apiForm, commissionType: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FIXED">Fixed (₹)</SelectItem>
                        <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>TDS %</Label>
                    <Input type="number" step="0.1" value={apiForm.tdsPercentage} onChange={(e) => setApiForm({ ...apiForm, tdsPercentage: parseFloat(e.target.value) || 0 })} />
                  </div>
                </div>
              )}

              {apiForm.pricingType === 'SLAB_BASED' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm font-medium text-yellow-800">⚡ Slab-Based Pricing Selected</p>
                  <p className="text-xs text-yellow-700 mt-1">After creating this API, go to the "Charge Slabs" tab to define amount-based pricing tiers.</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Amount (₹)</Label>
                <Input type="number" value={apiForm.minAmount} onChange={(e) => setApiForm({ ...apiForm, minAmount: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Max Amount (₹)</Label>
                <Input type="number" value={apiForm.maxAmount} onChange={(e) => setApiForm({ ...apiForm, maxAmount: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={apiForm.description}
                onChange={(e) => setApiForm({ ...apiForm, description: e.target.value })}
                placeholder="API description..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={apiForm.isActive} onCheckedChange={(v) => setApiForm({ ...apiForm, isActive: v })} />
              <Label>Active</Label>
            </div>

            {/* Merchant Assignment Section */}
            <div className="border-t pt-4">
              <div className="mb-4">
                <Label className="text-base font-medium flex items-center">
                  <Store className="h-4 w-4 mr-2" />
                  Assign to Merchants
                </Label>
                <p className="text-sm text-gray-600 mt-1">Select which merchants can use this API</p>
              </div>

              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                {merchants.length > 0 ? (
                  merchants.map((merchant) => (
                    <div key={merchant.merchantId || merchant.id} className="flex items-center space-x-2 mb-2">
                      <input
                        type="checkbox"
                        id={`merchant-${merchant.merchantId || merchant.id}`}
                        checked={selectedMerchants.includes(merchant.merchantId || merchant.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMerchants([...selectedMerchants, merchant.merchantId || merchant.id]);
                          } else {
                            setSelectedMerchants(selectedMerchants.filter(id => id !== (merchant.merchantId || merchant.id)));
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={`merchant-${merchant.merchantId || merchant.id}`} className="text-sm cursor-pointer">
                        <div>
                          <span className="font-medium">{merchant.merchantName || merchant.name || 'N/A'}</span>
                          <span className="text-gray-500 ml-2">({merchant.businessName || 'N/A'})</span>
                        </div>
                        <div className="text-xs text-gray-400">
                          ID: {merchant.merchantId || merchant.id} | Status: {merchant.status || 'N/A'}
                        </div>
                      </Label>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No merchants available</p>
                    <p className="text-xs">Please ensure merchants are active and verified</p>
                  </div>
                )}
              </div>

              {selectedMerchants.length > 0 && (
                <div className="mt-2 text-sm text-blue-600">
                  {selectedMerchants.length} merchant{selectedMerchants.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowApiDialog(false)}>Cancel</Button>
              <Button type="submit">{editingApi ? 'Update API' : 'Create API'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Slab Dialog */}
      <Dialog open={showSlabDialog} onOpenChange={setShowSlabDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSlab ? 'Edit Charge Slab' : 'Add Charge Slab'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitSlab} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Amount (₹) *</Label>
                <Input type="number" value={slabForm.minAmount} onChange={(e) => setSlabForm({ ...slabForm, minAmount: parseFloat(e.target.value) || 0 })} required />
              </div>
              <div>
                <Label>Max Amount (₹) *</Label>
                <Input type="number" value={slabForm.maxAmount} onChange={(e) => setSlabForm({ ...slabForm, maxAmount: parseFloat(e.target.value) || 0 })} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Charge Type *</Label>
                <Select value={slabForm.chargeType} onValueChange={(v) => setSlabForm({ ...slabForm, chargeType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FIXED">Fixed Amount (₹)</SelectItem>
                    <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Charge Value *</Label>
                <Input type="number" step="0.01" value={slabForm.chargeValue} onChange={(e) => setSlabForm({ ...slabForm, chargeValue: parseFloat(e.target.value) || 0 })} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST %</Label>
                <Input type="number" step="0.1" value={slabForm.gstPercentage} onChange={(e) => setSlabForm({ ...slabForm, gstPercentage: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Priority (lower = higher)</Label>
                <Input type="number" value={slabForm.priority} onChange={(e) => setSlabForm({ ...slabForm, priority: parseInt(e.target.value) || 0 })} />
              </div>
            </div>

            {selectedApi?.pricingType === 'COMMISSION' && (
              <div className="grid grid-cols-3 gap-4 p-3 bg-green-50 rounded">
                <div>
                  <Label>Commission Value</Label>
                  <Input type="number" step="0.01" value={slabForm.commissionValue} onChange={(e) => setSlabForm({ ...slabForm, commissionValue: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Commission Type</Label>
                  <Select value={slabForm.commissionType} onValueChange={(v) => setSlabForm({ ...slabForm, commissionType: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIXED">Fixed (₹)</SelectItem>
                      <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>TDS %</Label>
                  <Input type="number" step="0.1" value={slabForm.tdsPercentage} onChange={(e) => setSlabForm({ ...slabForm, tdsPercentage: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
            )}

            <div>
              <Label>Description</Label>
              <Input value={slabForm.description} onChange={(e) => setSlabForm({ ...slabForm, description: e.target.value })} placeholder="e.g., Amount 1-1000: Rs 5 + GST" />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={slabForm.isActive} onCheckedChange={(v) => setSlabForm({ ...slabForm, isActive: v })} />
              <Label>Active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSlabDialog(false)}>Cancel</Button>
              <Button type="submit">{editingSlab ? 'Update Slab' : 'Create Slab'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoItem = ({ label, value, className = '' }) => (
  <div className={`p-3 bg-gray-50 rounded ${className}`}>
    <span className="text-gray-500 text-xs block">{label}</span>
    <span className="font-medium">{value || '-'}</span>
  </div>
);

export default ApiConfig;
